import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// A neon sign: a dark backboard on two posts framed by a glowing tube border,
// with a bright lightning-bolt motif lit across it. A piece of powered signage.
const POST = "#5f656b";
const BOARD = "#23262b";
const TUBE = "#3fd0e0";
const BOLT = "#ffd23f";

// A thin glowing bar from a to b on the sign face (z fixed), for the bolt.
function bar(a: Vec3, b: Vec3, w: number, color: string): Part {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1e-4;
  const mid: Vec3 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, a[2]];
  const roll = Math.atan2(dy, dx) - Math.PI / 2;
  return { geometry: new THREE.BoxGeometry(w, len, 0.04), color, position: mid, rotation: [0, 0, roll] };
}

export const neonSign = defineModel({
  id: "neon-sign",
  batchId: "electric",
  name: "Neon sign",
  tags: ["prop", "electric", "power", "lighting", "sign", "neon"],
  summary: "A lit lightning-bolt neon sign on posts.",
  footprint: 0.5,
  parts(): Part[] {
    const z = 0.07;
    const parts: Part[] = [
      // Two posts.
      { geometry: new THREE.BoxGeometry(0.06, 0.7, 0.06), color: POST, position: [-0.36, 0.35, 0] },
      { geometry: new THREE.BoxGeometry(0.06, 0.7, 0.06), color: POST, position: [0.36, 0.35, 0] },
      // Backboard.
      { geometry: new THREE.BoxGeometry(0.94, 0.6, 0.08), color: BOARD, position: [0, 0.95, 0] },
    ];
    // Glowing tube border.
    parts.push(
      { geometry: new THREE.BoxGeometry(0.9, 0.04, 0.04), color: TUBE, position: [0, 1.23, z] },
      { geometry: new THREE.BoxGeometry(0.9, 0.04, 0.04), color: TUBE, position: [0, 0.67, z] },
      { geometry: new THREE.BoxGeometry(0.04, 0.6, 0.04), color: TUBE, position: [-0.45, 0.95, z] },
      { geometry: new THREE.BoxGeometry(0.04, 0.6, 0.04), color: TUBE, position: [0.45, 0.95, z] },
    );
    // Lightning-bolt motif (a zig-zag of bright bars).
    parts.push(
      bar([0.12, 1.18, z], [-0.06, 0.98, z], 0.07, BOLT),
      bar([-0.06, 0.98, z], [0.08, 0.96, z], 0.07, BOLT),
      bar([0.08, 0.96, z], [-0.1, 0.74, z], 0.07, BOLT),
    );
    return parts;
  },
  animations: [],
});
